import { createAuthClient } from 'better-auth/client'

/**
 * Browser-side auth client — talks to /api/auth/[...all] over HTTP.
 * Replaces the Supabase browser client's `.auth.*` methods 1:1 in spirit,
 * but the method names differ (Better-Auth: signIn.email / signUp.email /
 * signOut / getSession), so call sites were migrated to call
 * `authClient.signIn.email(...)` etc. directly instead of going through
 * this shim — see docs/migracao (T6 call site list) for what changed.
 */
export const authClient = createAuthClient({
  baseURL: process.env.NEXT_PUBLIC_APP_URL,
})

/**
 * IMPORTANT — architecture change from the Supabase era:
 *
 * The old browser client queried Postgres directly from the browser via
 * PostgREST (Supabase's anon-key + RLS HTTP API). Plain Postgres has no
 * browser-safe equivalent — a raw `pg` connection cannot run in a browser
 * bundle, and there is no PostgREST shim in this stack (out of scope to
 * build one; would be reinventing Supabase, which the brief explicitly
 * rules out).
 *
 * Consequence: every client-component call site that did
 * `createBrowserClient().from('table')...` needed to move its query to a
 * Server Action or Route Handler and be called over the network instead.
 * ~15 files did this (dashboard pages, Header, SearchLayout, etc. — see
 * grep 'createBrowserClient' apps/web/src). Migrating the .auth.* calls in
 * those files was in scope for this pass; migrating every .from() read/
 * write to a new Server Action is flagged as remaining T6 work (see final
 * report) since each one needs a bespoke, reviewed server action rather
 * than a generic proxy.
 *
 * This function exists so any call site NOT yet migrated fails loudly at
 * runtime (clear error, easy to grep for) instead of silently doing
 * nothing or leaking a `pg` import into the client bundle.
 */
export interface BrowserAuthUser {
  id: string
  email: string | null
  user_metadata?: Record<string, unknown>
  [key: string]: unknown
}

export function createBrowserClient(): {
  auth: {
    getUser: () => Promise<{ data: { user: BrowserAuthUser | null }; error: null }>
    getSession: () => Promise<{
      data: { session: { user: BrowserAuthUser } | null }
      error: null
    }>
    signOut: () => Promise<{ error: null }>
    /**
     * Supabase-shape shim over Better-Auth's `useSession` nanostore atom.
     * Fires immediately with the current session (`INITIAL_SESSION`) and
     * again on every change (`SIGNED_IN` / `SIGNED_OUT`) — Supabase call
     * sites only ever branch on `session?.user`, never the event string,
     * so the event names are best-effort labels, not load-bearing.
     */
    onAuthStateChange: (
      callback: (event: string, session: { user: BrowserAuthUser } | null) => void,
    ) => { data: { subscription: { unsubscribe: () => void } } }
  }
  from: (table: string) => never
} {
  return {
    auth: {
      async getUser() {
        const { data } = await authClient.getSession()
        return {
          data: { user: (data?.user ?? null) as unknown as BrowserAuthUser | null },
          error: null,
        }
      },
      async getSession() {
        const { data } = await authClient.getSession()
        return {
          data: {
            session: data
              ? ({ user: data.user as unknown as BrowserAuthUser } as { user: BrowserAuthUser })
              : null,
          },
          error: null,
        }
      },
      async signOut() {
        await authClient.signOut()
        return { error: null }
      },
      onAuthStateChange(callback) {
        let first = true
        const unsubscribe = authClient.useSession.subscribe((state) => {
          if (state.isPending) return
          const event = first ? 'INITIAL_SESSION' : state.data ? 'SIGNED_IN' : 'SIGNED_OUT'
          first = false
          callback(
            event,
            state.data
              ? ({ user: state.data.user as unknown as BrowserAuthUser } as {
                  user: BrowserAuthUser
                })
              : null,
          )
        })
        return { data: { subscription: { unsubscribe } } }
      },
    },
    from(table: string): never {
      throw new Error(
        `[@vagahub/db] createBrowserClient().from('${table}') is no longer supported — direct browser-to-Postgres queries were removed in the Supabase -> Postgres migration. Move this query into a Server Action and call it from the client component instead. See packages/db/src/browser.ts for context.`,
      )
    },
  }
}
